import { IconDownload, IconLoader, IconPlus } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { type ClientData, getClients } from '@/api/clients';
import { getDownloadUrl } from '@/api/file-upload-api';
import {
  type GenerateReportParams,
  generateReport,
  listReports,
  type PaginatedReports,
  type ReportListItem,
} from '@/api/reports';
import { type ColumnConfig, DataTable, type DataTableResponse } from '@/components/data-table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatFileSize(bytes: string | null): string {
  if (!bytes) return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n === 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<PaginatedReports | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [clients, setClients] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const fetchReports = useCallback(async (p: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listReports(p, 20);
      setReports(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports(page);
  }, [page, fetchReports]);

  const fetchClients = useCallback(async () => {
    if (clients.length > 0) return;
    setIsLoadingClients(true);
    try {
      const res = await getClients({ limit: 100, sortBy: 'name', order: 'asc' });
      setClients(
        res.data.map((c: ClientData) => ({
          value: c.id,
          label: c.name,
        }))
      );
    } catch {
      toast.error('Failed to load client list');
    } finally {
      setIsLoadingClients(false);
    }
  }, [clients.length]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    fetchClients();
  };

  const handleDownload = async (report: ReportListItem) => {
    try {
      const downloadData = await getDownloadUrl(report.id);
      window.open(downloadData.signed_url, '_blank');
      toast.success(`Downloading ${report.file_name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download report');
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const params: GenerateReportParams = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (selectedClients.length > 0) params.partyIds = selectedClients;

      const result = await generateReport(params);
      toast.success(
        `Report generated — ${result.summary.totalRows} rows in ${(result.summary.generationDurationMs / 1000).toFixed(1)}s`
      );
      setIsModalOpen(false);
      resetForm();
      setPage(1);
      fetchReports(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  const resetForm = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedClients([]);
  };

  const columns: ColumnConfig<ReportListItem>[] = [
    {
      key: 'created_at',
      label: 'Generated At',
      sortable: false,
      render: (item) => (
        <span className="text-sm whitespace-nowrap">{formatDate(item.created_at)}</span>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      sortable: false,
      render: (item) => (
        <span className="text-sm text-muted-foreground">
          {item.description?.replace('Payment File Ledger report — ', '') ?? '—'}
        </span>
      ),
    },
    {
      key: 'rows',
      label: 'Rows',
      sortable: false,
      align: 'right',
      render: (item) => (
        <span className="text-sm tabular-nums">
          {item.processing_metadata?.runtime_stats?.rows_processed ?? '—'}
        </span>
      ),
    },
    {
      key: 'size',
      label: 'Size',
      sortable: false,
      align: 'right',
      render: (item) => (
        <span className="text-sm tabular-nums">{formatFileSize(item.file_size_bytes)}</span>
      ),
    },
    {
      key: 'created_by',
      label: 'Generated By',
      sortable: false,
      render: (item) => <span className="text-sm">{item.created_by_name ?? 'System'}</span>,
    },
    {
      key: 'download',
      label: '',
      sortable: false,
      align: 'right',
      render: (item) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(item);
          }}
        >
          <IconDownload className="size-4" />
          <span className="sr-only">Download</span>
        </Button>
      ),
    },
  ];

  const tableData: DataTableResponse<ReportListItem> | undefined = reports
    ? {
        data: reports.data,
        pagination: {
          page: reports.pagination.page,
          pageSize: reports.pagination.limit,
          total: reports.pagination.total,
          totalPages: reports.pagination.totalPages,
        },
      }
    : undefined;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
            <p className="text-sm text-muted-foreground">
              Generate and download payment file ledger exports
            </p>
          </div>
          <Button onClick={handleOpenModal} className="shrink-0 gap-2">
            <IconPlus className="h-4 w-4" />
            Generate New
          </Button>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <DataTable<ReportListItem>
          data={tableData}
          columns={columns}
          caption="Reports"
          // Generation time is what tells two ledger reports apart -- the scope
          // repeats across runs and is blank on some rows.
          rowHeaderColumnKey="created_at"
          isLoading={isLoading}
          error={error}
          onPageChange={(p) => setPage(p)}
          emptyMessage="No reports generated yet. Click Generate New to create one."
        />
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Payment File Ledger</DialogTitle>
            <DialogDescription>
              Select a date range and clients to include. Leave fields empty to include all.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 overflow-visible">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dateFrom">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateTo">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reports-clients-filter">Clients</Label>
              {isLoadingClients ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <IconLoader className="size-4 animate-spin" />
                  Loading clients…
                </div>
              ) : (
                <MultiSelect
                  id="reports-clients-filter"
                  value={selectedClients}
                  onValueChange={setSelectedClients}
                  placeholder="All clients"
                  options={clients}
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <IconLoader className="mr-1 size-4 animate-spin" />
                  Generating…
                </>
              ) : (
                'Generate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
